import React from 'react';
import { useForm } from 'react-hook-form';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { ForgotPasswordRequest } from '../../../types/auth';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

interface ForgotPasswordFormProps {
  onSubmit: (data: ForgotPasswordRequest) => void;
  isLoading: boolean;
  error: string | null;
  success: boolean;
}

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({
  onSubmit,
  isLoading,
  error,
  success,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordRequest>();

  if (success) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 4,
            width: '100%',
            maxWidth: 400,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3,
            textAlign: 'center',
          }}
        >
          <CheckCircleOutlineIcon color="success" sx={{ fontSize: 64 }} />
          <Box>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              이메일 전송 완료
            </Typography>
            <Typography variant="body2" color="text.secondary">
              비밀번호 재설정 링크가 이메일로 전송되었습니다.
              <br />
              메일함을 확인해주세요.
            </Typography>
          </Box>
          <Button
            component={Link}
            to="/auth/login"
            variant="outlined"
            fullWidth
            size="large"
            sx={{ height: 48 }}
          >
            로그인으로 돌아가기
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 4,
          width: '100%',
          maxWidth: 400,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 1 }}>
          <Typography component="h1" variant="h5" fontWeight="bold" gutterBottom>
            비밀번호 찾기
          </Typography>
          <Typography variant="body2" color="text.secondary">
            가입한 이메일 주소를 입력해주세요
          </Typography>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              required
              fullWidth
              id="email"
              label="이메일"
              autoComplete="email"
              autoFocus
              error={!!errors.email}
              helperText={errors.email?.message}
              disabled={isLoading}
              {...register('email', {
                required: '이메일을 입력해주세요',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: '올바른 이메일 형식이 아닙니다',
                },
              })}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isLoading}
              sx={{
                mt: 1,
                height: 48,
                fontWeight: 'bold',
                boxShadow: 'none',
                '&:hover': {
                  boxShadow: 'none',
                },
              }}
            >
              {isLoading ? <CircularProgress size={24} /> : '이메일 전송'}
            </Button>

            <Box sx={{ textAlign: 'center', mt: 1 }}>
              <Link
                to="/auth/login"
                style={{
                  textDecoration: 'none',
                  color: 'inherit',
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    '&:hover': { textDecoration: 'underline' },
                    cursor: 'pointer',
                  }}
                >
                  로그인으로 돌아가기
                </Typography>
              </Link>
            </Box>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default ForgotPasswordForm;
